import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { MY_TEAM_STATUS } from '../../graphql/team-status';
import { useMyPrivileges } from '../../hooks/useMyPrivileges';
import {
  STATUS_DISPLAY,
  NOT_SET_DISPLAY,
  formatTime,
  buildContextLine,
} from './team-status-display';
import TeamStatusModal from './team-status-modal';

const TeamStatusWidget = ({ user, isLoggingOut = false }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { hasPrivilege, loading: privilegesLoading } = useMyPrivileges();

  const canShowWidget = hasPrivilege('TEAM_MEMBER_STATUS');

  const { data, loading } = useQuery(MY_TEAM_STATUS, {
    skip: !user || isLoggingOut || !canShowWidget,
    pollInterval: 60_000,
    fetchPolicy: 'cache-and-network',
  });

  if (!user) return null;
  if (privilegesLoading) return null;
  if (!canShowWidget) return null;

  const status = data?.myTeamStatus;
  const display = status?.status ? STATUS_DISPLAY[status.status] : NOT_SET_DISPLAY;
  const fallback = display || NOT_SET_DISPLAY;
  const contextLine = buildContextLine(status);
  const setAtTime = formatTime(status?.statusSetAt);
  const isAuto = status?.contextSource && status.contextSource !== 'MANUAL';

  const handleOpen = () => setIsModalOpen(true);
  const handleClose = () => setIsModalOpen(false);
  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleOpen();
    }
  };

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        aria-label="Update your status"
        onClick={handleOpen}
        onKeyDown={handleKeyDown}
        className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 hover:shadow-md hover:border-sky-300 cursor-pointer transition focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="relative flex-shrink-0">
              <span className={`block w-3 h-3 rounded-full ${fallback.dot}`} />
              {status?.status && (
                <span className={`absolute inset-0 w-3 h-3 rounded-full ${fallback.dot} animate-ping opacity-60`} />
              )}
            </div>
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-wide text-gray-500 font-medium">
                My Status
              </div>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span
                  className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${fallback.badge}`}
                >
                  {fallback.label}
                </span>
                {status?.status === 'IN_OFFICE' && status.officeName && (
                  <span className="text-sm font-semibold text-gray-800 truncate">
                    {status.officeName}
                  </span>
                )}
                {isAuto && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-sky-700 bg-sky-50 border border-sky-100 px-1.5 py-0.5 rounded">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Auto via GPS
                  </span>
                )}
              </div>
              {(contextLine || setAtTime) && (
                <div className="text-xs text-gray-500 mt-1 truncate">
                  {contextLine || (setAtTime ? `Set at ${setAtTime}` : null)}
                </div>
              )}
              {!status?.status && !loading && (
                <div className="text-xs text-gray-500 mt-1">
                  Tap to check in for the day
                </div>
              )}
            </div>
          </div>
          <div className="flex-shrink-0 inline-flex items-center px-3 py-1.5 border border-sky-600 text-xs font-medium rounded-md text-sky-600 bg-white">
            Update
            <svg className="w-3 h-3 ml-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>
      </div>

      <TeamStatusModal
        isOpen={isModalOpen}
        onClose={handleClose}
        currentStatus={status?.status || null}
        currentOfficeName={status?.officeName || null}
      />
    </>
  );
};

export default TeamStatusWidget;
